import { useCallback, useState } from 'react';
import { CLASSIFICATION_ORDER, PARAM_LABELS, SOIL_GROUP_CODES } from '../constants/parameters.constants';
import { SoilRuleSchema } from '../schemas/parameters.schema';
import type { SoilCard, SoilGroupKey, StateReferenceDTO } from '../types/parameters.types';
import { toFieldErrors, type FieldErrors } from '../utils/formErrors';
import { applySoilEdit, buildSoilRule, type SoilRuleForm, type SoilRuleUpdate } from '../utils/soilRuleBuilder';

interface UseSoilRuleFormParams {
	group: SoilGroupKey;
	reference: StateReferenceDTO;
	card: SoilCard | null;
	onSubmit: (update: SoilRuleUpdate) => Promise<boolean>;
	onClose: () => void;
}

const blankClasses = () => CLASSIFICATION_ORDER.map((classification) => ({ classification }));

const blankForm = (code: string, reference: StateReferenceDTO): SoilRuleForm => {
	const dependent = reference.dependentParams.find((param) => param.code === code);
	const fixed = reference.fixedParams.find((param) => param.code === code);
	const existing = dependent ?? fixed;

	return {
		code,
		kind: dependent?.rules[0]?.criterion ?? (fixed || !code.startsWith('Pm1') ? 'fixed' : 'Prem'),
		clayMode: 'texture',
		texture: 'arenosa',
		name: existing?.name ?? PARAM_LABELS[code] ?? code,
		method: existing?.method ?? '',
		unit: existing?.unit ?? '',
		idealRange: '',
		classes: blankClasses(),
	};
};

const formFromCard = (card: SoilCard): SoilRuleForm => ({
	code: card.code,
	kind: card.kind,
	clayMode: card.texture ? 'texture' : 'range',
	texture: card.texture ?? 'arenosa',
	minValue: card.minValue ?? undefined,
	maxValue: card.maxValue ?? undefined,
	name: card.name ?? '',
	method: card.method ?? '',
	unit: card.unit ?? '',
	idealRange: card.idealRange ?? '',
	classes: card.classes,
});

export const usesBand = (form: SoilRuleForm) =>
	form.kind === 'Prem' || (form.kind === 'clay' && form.clayMode === 'range');

export const useSoilRuleForm = ({ group, reference, card, onSubmit, onClose }: UseSoilRuleFormParams) => {
	const [form, setForm] = useState<SoilRuleForm>(() =>
		card ? formFromCard(card) : blankForm(SOIL_GROUP_CODES[group][0], reference)
	);
	const [errors, setErrors] = useState<FieldErrors>({});

	const setField = useCallback(
		<K extends keyof SoilRuleForm>(key: K, value: SoilRuleForm[K]) => {
			setErrors({});
			setForm((previous) =>
				key === 'code'
					? { ...blankForm(value as string, reference), classes: previous.classes }
					: { ...previous, [key]: value }
			);
		},
		[reference]
	);

	const submit = async () => {
		const parsed = SoilRuleSchema.safeParse({ ...form, usesBand: usesBand(form) });
		if (!parsed.success) {
			setErrors(toFieldErrors(parsed.error));
			return;
		}
		const result = card ? applySoilEdit(reference, card, form) : buildSoilRule(reference, form);
		if (!result.ok) {
			setErrors({ form: result.error });
			return;
		}
		if (await onSubmit(result.update)) onClose();
	};

	return { form, errors, setField, submit };
};
